import { useState } from 'react'
import { Database, Globe, Radio, FileUp, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { DbVendor, IdentityMatchMode, SourceType, SqlDialect, WatermarkMode } from '../types'
import { IDENTITY_MATCH_LABELS } from '../types'
import { Select } from '@/components/ui/select'

/**
 * Choosing how punches reach the system.
 *
 * <p>The four routes are not interchangeable in practice — which one is available is
 * usually decided by the customer's existing installation, not by preference — so each
 * option says plainly when it applies rather than just naming a protocol.
 */
const SOURCE_OPTIONS: {
  value: SourceType
  label: string
  when: string
  icon: typeof Radio
}[] = [
  {
    value: 'ADMS_PUSH',
    label: 'Device push',
    when: 'Terminals can reach this server and you can change their settings. Real time.',
    icon: Radio,
  },
  {
    value: 'SQL_DATABASE',
    label: 'Vendor database',
    when: 'ZKBio Time or ZKTime already owns the devices and must stay the system of record.',
    icon: Database,
  },
  {
    value: 'BIOTIME_REST',
    label: 'Vendor API',
    when: 'The vendor software exposes its HTTP API and you would rather not read its database.',
    icon: Globe,
  },
  {
    value: 'CSV_IMPORT',
    label: 'File upload',
    when: 'Nothing else is reachable. An operator uploads punch files.',
    icon: FileUp,
  },
]

const SQL_DIALECTS: { value: SqlDialect; label: string; hint: string }[] = [
  {
    value: 'BIOTIME_8',
    label: 'BioTime 8',
    hint: 'Starts from iclock_transaction keyed on emp_code. Every name below can be changed.',
  },
  {
    value: 'ZKTIME_LEGACY',
    label: 'ZKTime / ZKAccess',
    hint: 'Starts from CHECKINOUT joined to USERINFO for the badge number.',
  },
  {
    value: 'MAPPED',
    label: 'Something else',
    hint: 'Give the table and column names and we build the query.',
  },
  { value: 'CUSTOM', label: 'Custom query', hint: 'Supply your own read-only SELECT.' },
]

const DB_VENDORS: { value: DbVendor; label: string }[] = [
  { value: 'POSTGRESQL', label: 'PostgreSQL' },
  { value: 'SQLSERVER', label: 'SQL Server' },
  { value: 'MYSQL', label: 'MySQL' },
]

/**
 * The names the presets start from.
 *
 * Shown as placeholders rather than filled in, so an empty box means "use the default" and
 * a filled one means "this install is different" — which is exactly the distinction an
 * administrator needs to see at a glance.
 */
const PRESET_PLACEHOLDERS: Record<string, Record<string, string>> = {
  BIOTIME_8: {
    sourceTable: 'iclock_transaction',
    columnId: 'id',
    columnPin: 'emp_code',
    columnPunchTime: 'punch_time',
    columnPunchState: 'punch_state',
    columnVerifyType: 'verify_type',
    columnDeviceSn: 'terminal_sn',
    columnWorkCode: 'work_code',
  },
  ZKTIME_LEGACY: {
    sourceTable: 'CHECKINOUT',
    columnPin: 'u.Badgenumber',
    columnPunchTime: 't.CHECKTIME',
    columnPunchState: 't.CHECKTYPE',
    columnVerifyType: 't.VERIFYCODE',
    columnDeviceSn: 't.SENSORID',
  },
}

const COLUMN_FIELDS: { key: keyof SourceFormValues; label: string; required?: boolean }[] = [
  { key: 'columnPin', label: 'Employee identifier', required: true },
  { key: 'columnPunchTime', label: 'Punch time', required: true },
  { key: 'columnId', label: 'Row id' },
  { key: 'columnPunchState', label: 'Punch state' },
  { key: 'columnVerifyType', label: 'Verify type' },
  { key: 'columnDeviceSn', label: 'Terminal serial' },
  { key: 'columnWorkCode', label: 'Work code' },
]

export interface SourceFormValues {
  name: string
  type: SourceType
  pollIntervalMinutes: number
  sqlDialect?: SqlDialect
  jdbcUrl?: string
  databaseUsername?: string
  databasePassword?: string
  sqlQuery?: string
  apiBaseUrl?: string
  apiUsername?: string
  apiPassword?: string
  apiToken?: string

  dbVendor?: DbVendor
  dbSchema?: string
  sourceTable?: string
  sourceJoin?: string
  columnId?: string
  columnPin?: string
  columnPunchTime?: string
  columnPunchState?: string
  columnVerifyType?: string
  columnDeviceSn?: string
  columnWorkCode?: string
  watermarkMode?: WatermarkMode
  identityMatchMode?: IdentityMatchMode
  identityMatchField?: string
  batchSize?: number
}

export function SourceForm({
  onSubmit,
  submitting,
}: {
  onSubmit: (values: SourceFormValues) => void
  submitting?: boolean
}) {
  const [values, setValues] = useState<SourceFormValues>({
    name: '',
    type: 'ADMS_PUSH',
    pollIntervalMinutes: 5,
    sqlDialect: 'BIOTIME_8',
    dbVendor: 'POSTGRESQL',
    watermarkMode: 'TIMESTAMP',
    identityMatchMode: 'EMPLOYEE_CODE',
    batchSize: 5000,
  })

  function set<K extends keyof SourceFormValues>(key: K, value: SourceFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }))
  }

  const isSql = values.type === 'SQL_DATABASE'
  const isApi = values.type === 'BIOTIME_REST'
  const isPolled = isSql || isApi

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit(values)
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="source-name" required>
          Name
        </Label>
        <Input
          id="source-name"
          required
          value={values.name}
          onChange={(event) => set('name', event.target.value)}
          placeholder="Head office terminals"
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-[var(--color-heading)]">How punches arrive</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {SOURCE_OPTIONS.map((option) => {
            const Icon = option.icon
            const selected = values.type === option.value
            return (
              <label
                key={option.value}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-[var(--radius-control)] border p-3 transition-colors',
                  selected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                    : 'border-[var(--color-border)] hover:bg-[var(--color-page)]',
                )}
              >
                <input
                  type="radio"
                  name="sourceType"
                  className="sr-only"
                  checked={selected}
                  onChange={() => set('type', option.value)}
                />
                <Icon
                  className={cn('mt-0.5 size-4 shrink-0', selected ? 'text-[var(--color-primary)]' : 'text-[var(--color-muted)]')}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[var(--color-heading)]">{option.label}</span>
                  <span className="block text-xs leading-snug text-[var(--color-muted)]">{option.when}</span>
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>

      {values.type === 'ADMS_PUSH' && (
        <p className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-blue)] px-3 py-2.5 text-sm text-[var(--color-body)]">
          <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-info)]" aria-hidden="true" />
          <span>
            Point each terminal's ADMS server setting at this host, then approve its serial number
            on the Devices tab when it first checks in.
          </span>
        </p>
      )}

      {isSql && (
        <div className="space-y-4 rounded-[var(--radius-control)] border border-[var(--color-border)] p-4">
          <div className="space-y-1.5">
            <Label htmlFor="sql-dialect">Database schema</Label>
            <Select
              id="sql-dialect"
              value={values.sqlDialect}
              onChange={(event) => set('sqlDialect', event.target.value as SqlDialect)}
              className="w-full"
            >
              {SQL_DIALECTS.map((dialect) => (
                <option key={dialect.value} value={dialect.value}>
                  {dialect.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-[var(--color-muted)]">
              {SQL_DIALECTS.find((dialect) => dialect.value === values.sqlDialect)?.hint}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="jdbc-url" required>
              JDBC URL
            </Label>
            <Input
              id="jdbc-url"
              required
              value={values.jdbcUrl ?? ''}
              onChange={(event) => set('jdbcUrl', event.target.value)}
              placeholder="jdbc:postgresql://10.0.0.9:5432/biotime"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="db-user">Username</Label>
              <Input
                id="db-user"
                autoComplete="off"
                value={values.databaseUsername ?? ''}
                onChange={(event) => set('databaseUsername', event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="db-pass">Password</Label>
              <Input
                id="db-pass"
                type="password"
                autoComplete="new-password"
                value={values.databasePassword ?? ''}
                onChange={(event) => set('databasePassword', event.target.value)}
              />
            </div>
          </div>

          {values.sqlDialect !== 'CUSTOM' && (
            <div className="space-y-4 border-t border-[var(--color-border)] pt-4">
              <p className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-blue)] px-3 py-2.5 text-sm text-[var(--color-body)]">
                <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-info)]" aria-hidden="true" />
                <span>
                  ZKTeco documents its API, not its database schema. These names are what we
                  see on a typical install — leave a box empty to use it, or type the name
                  yours actually uses. Test connection will tell you which ones the server
                  rejected.
                </span>
              </p>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="db-vendor">Database</Label>
                  <Select
                    id="db-vendor"
                    value={values.dbVendor ?? 'POSTGRESQL'}
                    onChange={(event) => set('dbVendor', event.target.value as DbVendor)}
                    className="w-full"
                  >
                    {DB_VENDORS.map((vendor) => (
                      <option key={vendor.value} value={vendor.value}>
                        {vendor.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="db-schema">Schema</Label>
                  <Input
                    id="db-schema"
                    value={values.dbSchema ?? ''}
                    onChange={(event) => set('dbSchema', event.target.value)}
                    placeholder="public"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="source-table" required={values.sqlDialect === 'MAPPED'}>
                    Table
                  </Label>
                  <Input
                    id="source-table"
                    required={values.sqlDialect === 'MAPPED'}
                    value={values.sourceTable ?? ''}
                    onChange={(event) => set('sourceTable', event.target.value)}
                    placeholder={PRESET_PLACEHOLDERS[values.sqlDialect ?? '']?.sourceTable ?? 'punches'}
                  />
                </div>
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-[var(--color-heading)]">Column names</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {COLUMN_FIELDS.map((field) => (
                    <div key={field.key} className="space-y-1.5">
                      <Label htmlFor={`col-${field.key}`} required={field.required && values.sqlDialect === 'MAPPED'}>
                        {field.label}
                      </Label>
                      <Input
                        id={`col-${field.key}`}
                        value={(values[field.key] as string | undefined) ?? ''}
                        onChange={(event) => set(field.key, event.target.value as never)}
                        placeholder={PRESET_PLACEHOLDERS[values.sqlDialect ?? '']?.[field.key as string] ?? ''}
                      />
                    </div>
                  ))}
                </div>
              </fieldset>

              <div className="space-y-1.5">
                <Label htmlFor="source-join">Extra join</Label>
                <Input
                  id="source-join"
                  value={values.sourceJoin ?? ''}
                  onChange={(event) => set('sourceJoin', event.target.value)}
                  placeholder="JOIN USERINFO u ON u.USERID = t.USERID"
                  className="font-mono text-xs"
                />
                <p className="text-xs text-[var(--color-muted)]">
                  For layouts that keep the badge number in a second table. The punch table is
                  aliased <code>t</code>. Read-only: write statements are rejected.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="identity-mode">Match punches to employees by</Label>
                  <Select
                    id="identity-mode"
                    value={values.identityMatchMode ?? 'EMPLOYEE_CODE'}
                    onChange={(event) => set('identityMatchMode', event.target.value as IdentityMatchMode)}
                    className="w-full"
                  >
                    {(Object.keys(IDENTITY_MATCH_LABELS) as IdentityMatchMode[]).map((mode) => (
                      <option key={mode} value={mode}>
                        {IDENTITY_MATCH_LABELS[mode]}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-[var(--color-muted)]">
                    Get this wrong and every punch imports unmatched while the sync reports
                    success.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="watermark-mode">Read new punches by</Label>
                  <Select
                    id="watermark-mode"
                    value={values.watermarkMode ?? 'TIMESTAMP'}
                    onChange={(event) => set('watermarkMode', event.target.value as WatermarkMode)}
                    className="w-full"
                  >
                    <option value="TIMESTAMP">Punch time</option>
                    <option value="ID">Row id</option>
                  </Select>
                  <p className="text-xs text-[var(--color-muted)]">
                    Row id survives the source's clock moving backwards; punch time does not.
                  </p>
                </div>
              </div>

              {values.identityMatchMode === 'CUSTOM_FIELD' && (
                <div className="space-y-1.5">
                  <Label htmlFor="identity-field" required>
                    Custom field name
                  </Label>
                  <Input
                    id="identity-field"
                    required
                    value={values.identityMatchField ?? ''}
                    onChange={(event) => set('identityMatchField', event.target.value)}
                    placeholder="biotimeCode"
                  />
                </div>
              )}
            </div>
          )}

          {values.sqlDialect === 'CUSTOM' && (
            <div className="space-y-1.5">
              <Label htmlFor="sql-query" required>
                Query
              </Label>
              <textarea
                id="sql-query"
                required
                rows={5}
                value={values.sqlQuery ?? ''}
                onChange={(event) => set('sqlQuery', event.target.value)}
                placeholder={'SELECT pin, punch_time, punch_state\n  FROM my_punches\n WHERE punch_time > ?\n ORDER BY punch_time'}
                className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 font-mono text-xs"
              />
              <p className="text-xs text-[var(--color-muted)]">
                Must return <code>pin</code> and <code>punch_time</code>, and take one{' '}
                <code>?</code> bound to the last imported punch. Read-only: write statements are
                rejected.
              </p>
            </div>
          )}
        </div>
      )}

      {isApi && (
        <div className="space-y-4 rounded-[var(--radius-control)] border border-[var(--color-border)] p-4">
          <div className="space-y-1.5">
            <Label htmlFor="api-url" required>
              Base URL
            </Label>
            <Input
              id="api-url"
              required
              value={values.apiBaseUrl ?? ''}
              onChange={(event) => set('apiBaseUrl', event.target.value)}
              placeholder="https://biotime.example.com"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="api-user">Username</Label>
              <Input
                id="api-user"
                autoComplete="off"
                value={values.apiUsername ?? ''}
                onChange={(event) => set('apiUsername', event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="api-pass">Password</Label>
              <Input
                id="api-pass"
                type="password"
                autoComplete="new-password"
                value={values.apiPassword ?? ''}
                onChange={(event) => set('apiPassword', event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="api-token">API token</Label>
            <Input
              id="api-token"
              type="password"
              autoComplete="off"
              value={values.apiToken ?? ''}
              onChange={(event) => set('apiToken', event.target.value)}
              placeholder="Use instead of a username and password, if you have one"
            />
          </div>
        </div>
      )}

      {isPolled && (
        <div className="space-y-1.5">
          <Label htmlFor="poll-interval">Check every</Label>
          <div className="flex items-center gap-2">
            <Input
              id="poll-interval"
              type="number"
              min={1}
              max={1440}
              value={values.pollIntervalMinutes}
              onChange={(event) => set('pollIntervalMinutes', Number(event.target.value))}
              className="w-28"
            />
            <span className="text-sm text-[var(--color-muted)]">minutes</span>
          </div>
        </div>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? 'Saving...' : 'Add source'}
      </Button>
    </form>
  )
}
